import React, {useEffect, useState} from 'react';
import DateFnsUtils from '@date-io/date-fns';
import {CSVLink} from "react-csv";
import {
    Box,
    Button,
    Container,
    Chip,
    CircularProgress,
    ExpansionPanel,
    ExpansionPanelSummary,
    ExpansionPanelDetails,
    Grid,
    IconButton,
    Modal,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Switch
} from '@material-ui/core';
import {
    MuiPickersUtilsProvider,
    KeyboardDateTimePicker,
} from '@material-ui/pickers';
import {Autocomplete, Pagination} from '@material-ui/lab';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import RefreshIcon from '@material-ui/icons/Refresh';
import DateRangeIcon from '@material-ui/icons/DateRange';
import VisibilityIcon from '@material-ui/icons/Visibility';
import useStyles from '../styles/styles';
import {useQuery} from '@apollo/react-hooks';
import {gql} from 'apollo-boost';
import {navigate} from '@reach/router';
import useFormInputs from "../hooks/useFormInputs";
import _ from 'lodash';
import dashes from 'add-dashes-to-uuid';
import RevenueItemsSearchTable from "../components/RevenueItems/RevenueItemsSearchTable";
import ReceiptIcon from "@material-ui/icons/Receipt";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import TrafficIcon from "@material-ui/icons/Traffic";
import AddIcon from "@material-ui/icons/Add";

/**
 *
 * @param {*} props
 */
const RevenueItems = () => {
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    const classes = useStyles();
    const defaultQuery = {
        startDate: startOfDay.toISOString()
    };
    const [variables, setVariables] = useState(defaultQuery);
    const [open, setOpen] = useState(false);
    const [csvData, setCSVData] = useState(false);
    const [paginationVal, setPaginationVal] = useState({
        skipVal: 0,
        currentPage: 1,
        limit: 30,
        numberOfPages: 0,
        totalCount: 0
    });
    const [filterTrafficSources, setFilterTrafficSources] = useState([]);
    const [activeColumns, setActiveColumns] = useState(RevenueItemsSearchTable());
    const [configColumns, setConfigColumns] = useState(activeColumns);
    const onSubmit = () => {
        const finalInputs = _.omitBy(inputs, _.isEmpty);
        if (_.isEqual(variables, finalInputs)) {
            refetch();
        }
        const startDate = finalInputs.startDate === undefined ? startOfDay : finalInputs.startDate;

        setPaginationVal({
            ...paginationVal,
            limit: 30,
            skipVal: 0
        });
        if (_.has(finalInputs, 'search')) {
            const searchId = finalInputs.search.includes('-') ? finalInputs.search : dashes(finalInputs.search);
            navigate(`/info/revenueitems/${searchId}`);
        } else {
            setVariables({
                ...finalInputs,
                startDate,
            });
        }
    };

    const {
        clearInput,
        clearAllInputs,
        inputs,
        setInputs,
        handleFilterChange,
        handleInputChange,
        handleSubmit
    } = useFormInputs(onSubmit);
    const {loading, error, data, refetch, networkStatus} = useQuery(gql`
        query AllRevenue(
            $startDate: DateTime
            $endDate: DateTime
            $affiliateId: [UUID!]
            $trafficSourceId: [UUID!]
            $everflowTransactionId: String
            $limit: Int
            $skip: Long
        ) {
            allRevenue(
                limit: $limit
                skip: $skip
                input: {
                    everflowTransactionId: $everflowTransactionId
                    createdAt: {gte: $startDate, lte: $endDate}
                    trafficSource:{
                        id: $trafficSourceId,
                        affiliate:{
                            id: $affiliateId
                        }
                        includeDeleted: false
                    }
                }
            ) {
                totalCount
                nodes {
                    id
                    dateCreated
                    dateModified
                    identityId
                    leadDate
                    billingDate
                    revenueType
                    revenueClassification
                    trafficSourceId
                    affiliateId
                    sourceSubCode1
                    sourceSubCode2
                    sourceSubCode3
                    sourceSubCode4
                    sourceSubCode5
                    billed
                    payout
                    advCompanyId
                    adv1
                    adv2
                    adv3
                    adv4
                    adv5
                    loanRequestId
                    loanResultId
                    campaignDecisionLogId
                    formId
                    clickBrowserId
                    formFieldSubmitBrowserId
                    formFieldSubmitFieldId
                    formSubmitBrowserId
                    formSubmitDataPackageType
                    everflowStatus
                    everflowStatusExpiration
                    everflowAffiliateHash
                    everflowOfferHash
                    everflowTransactionId
                    everflowConversionId
                    adjustmentTotalBilled
                    adjustmentTotalPayout
                    adjustments {
                        id
                        revenueId
                        adjustmentType
                        adjustmentCategory
                        payout
                        billed
                        reason
                    }
                }
            }
            allAffiliate {
                nodes {
                    id
                    name
                }
            }
            allTrafficSource {
                nodes {
                    id
                    name
                    legacyPromoId
                    isDeleted
                    affiliate {
                        id
                    }
                }
            }
        }
    `, {
        variables: {
            ...variables,
            limit: paginationVal.limit,
            skip: paginationVal.skipVal,
        },
        notifyOnNetworkStatusChange: true,
        context: {
            clientName: "kgraphql"
        }
    });

    const allAffiliates = (data && data.allAffiliate && data.allAffiliate.nodes) || [];
    const allTrafficSources = (data && data.allTrafficSource && data.allTrafficSource.nodes) || [];
    const totalCount = (data && data.allRevenue && data.allRevenue.totalCount) || 0;
    const numberOfPages = totalCount > 0 ? Math.ceil(totalCount / paginationVal.limit) : 0;

    const handleClose = () => {
        setOpen(false);
    };

    useEffect(() => {
        if (data && Object.keys(data).length) {
            if (inputs.startDate === undefined) {
                setInputs({
                    startDate: startOfDay.toISOString()
                });
            }
            setPaginationVal(p => ({
                ...p,
                totalCount,
                numberOfPages: numberOfPages === 0 ? 1 : numberOfPages
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (inputs) {
            if (inputs.affiliateId) {
                const arr = allTrafficSources.filter(item => item.affiliate.id === inputs.affiliateId);
                setFilterTrafficSources(arr);
            } else {
                setFilterTrafficSources([]);
            }
        }
    }, [inputs])

    /**
     * @param {array} options - array of options for input
     * @param {string} searchKey - value to search on
     * @param {string} keyVal - key value to reference
     * @param {array} optionKeys - key values to return
     */
    const getActiveVal = (options, searchKey, keyVal, optionKeys) => {
        const item = options.filter(item => searchKey ? item[searchKey] === inputs[keyVal] : item === inputs[keyVal]);
        if (item.length) {
            let optionLabel = "";
            if (optionKeys.length) {
                optionKeys.forEach(key => {
                    if (!optionLabel.length) {
                        optionLabel = item[0][key];
                    } else {
                        optionLabel = `${optionLabel} - ${item[0][key]}`
                    }
                });
            } else {
                optionLabel = item[0];
            }
            return optionLabel;
        }
        return null;
    };

    const AutoCompleteInput = ({options, searchKey, keyVal, label, optionKeys}) => {
        return (
            <Autocomplete
                className={classes.autoComplete}
                value={inputs[keyVal] || null}
                onChange={(event, value) => handleFilterChange(searchKey ? (value ? value[searchKey] : '') : (value ? value.replace(/['"]+/g, '') : ''), keyVal)}
                options={options}
                renderOption={option => {
                    let optionLabel = "";
                    if (optionKeys.length) {
                        optionKeys.forEach(key => {
                            if (!optionLabel.length) {
                                optionLabel = option[key];
                            } else {
                                optionLabel = `${optionLabel} - ${option[key]}`
                            }
                        });
                    } else {
                        optionLabel = option;
                    }
                    return optionLabel;
                }}
                getOptionLabel={option => {
                    const activeVal = getActiveVal(options, searchKey, keyVal, optionKeys);
                    return activeVal || (_.has(option, 'name') ? option.name : option);
                }}
                renderInput={params => <TextField {...params} label={label} fullWidth/>}
            />
        );
    };

    const RevenueItemsTable = () => {
        return (
            <Table className={classes.table} size="small">
                <TableHead>
                    <TableRow>
                        <TableCell variant="head">Action</TableCell>
                        {activeColumns.map(column => (
                            column.enabled &&
                            <TableCell
                                key={column.id}
                                variant="head"
                                align={column.alignment ? column.alignment : 'inherit'}
                            >
                                {column.title}
                            </TableCell>

                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading || networkStatus === 4 ? (
                        <TableRow key="graphql-loading">
                            <TableCell colSpan={12} style={{textAlign: "center"}}>
                                Loading…
                                <br/>
                                <CircularProgress
                                    size={128}
                                    thickness={4}
                                />
                            </TableCell>
                        </TableRow>
                    ) : (error ? (
                        <TableRow key="graphql-error">
                            <TableCell colSpan={12}>
                                <pre>{JSON.stringify(error, null, 2)}</pre>
                            </TableCell>
                        </TableRow>
                    ) : (data && data.allRevenue.nodes.map((revenueItem) => (
                        <TableRow key={revenueItem.id}>
                            <>
                                <TableCell variant="body">
                                    <div>
                                        <VisibilityIcon
                                            onClick={() => window.open(`/info/revenueitems/${revenueItem.id}`)}
                                        />
                                    </div>
                                </TableCell>
                                {activeColumns.map(column => (
                                    column.enabled &&
                                    <TableCell
                                        key={column.id}
                                        variant="body"
                                        align={column.alignment ? column.alignment : 'inherit'}
                                    >
                                        {column.data(revenueItem)}
                                    </TableCell>
                                ))}
                            </>
                        </TableRow>
                    ))))}
                </TableBody>
            </Table>
        );
    };

    return (
        <>
            <Typography variant="h2">
                Revenue
            </Typography>
            <Container maxWidth={false} className={classes.chips}>
                {inputs.startDate || inputs.endDate ? (
                    <Chip
                        icon={<DateRangeIcon/>}
                        label="Date Range"
                        clickable
                        onClick={() => {
                            clearInput('startDate')
                            clearInput('endDate')
                        }}
                        onDelete={() => {
                            clearInput('startDate')
                            clearInput('endDate')
                        }}
                    />
                ) : null}
                {inputs.affiliateId ? (
                    <Chip
                        icon={<SupervisorAccountIcon/>}
                        label="Affilates"
                        clickable
                        onClick={() => clearInput('affiliateId')}
                        onDelete={() => clearInput('affiliateId')}
                    />
                ) : null}
                {inputs.trafficSourceId ? (
                    <Chip
                        icon={<TrafficIcon/>}
                        label="Traffic Sources"
                        clickable
                        onClick={() => clearInput('trafficSourceId')}
                        onDelete={() => clearInput('trafficSourceId')}
                    />
                ) : null}
                {inputs.everflowTransactionId ? (
                    <Chip
                        icon={<ReceiptIcon/>}
                        label="Everflow Transaction Id"
                        color="primary"
                        clickable
                        onClick={() => clearInput('everflowTransactionId')}
                        onDelete={() => clearInput('everflowTransactionId')}
                    />
                ) : null}
            </Container>
            <form id="revenue-items-form" onSubmit={handleSubmit}>
                <ExpansionPanel expanded>
                    <ExpansionPanelSummary
                        expandIcon={<ExpandMoreIcon/>}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                    >
                        <Typography variant="h5">Filter Revenue</Typography>
                    </ExpansionPanelSummary>
                    <ExpansionPanelDetails className={classes.filter}>
                        <Container maxWidth={false}>
                            <Box className={classes.filterBox} style={{marginTop: 0}}>
                                <Chip
                                    label="Default Options"
                                    className={classes.filterSectionLabels}
                                />
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            id="search"
                                            name="search"
                                            label="Search by UUID"
                                            value={inputs.search}
                                            onChange={handleInputChange}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            id="everflowTransactionId"
                                            name="everflowTransactionId"
                                            label="Search by Everflow Transaction Id"
                                            value={inputs.everflowTransactionId}
                                            onChange={handleInputChange}
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={2}>
                                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                            <KeyboardDateTimePicker
                                                disableFuture
                                                ampm={false}
                                                variant="inline"
                                                margin="normal"
                                                id="date-picker-start"
                                                label="Start Date"
                                                value={inputs.startDate || null}
                                                onChange={date => handleFilterChange(date.toISOString(), 'startDate')}
                                                KeyboardButtonProps={{
                                                    'aria-label': 'change date',
                                                }}
                                                className={classes.formControl}
                                            />
                                        </MuiPickersUtilsProvider>
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                            <KeyboardDateTimePicker
                                                disableFuture
                                                ampm={false}
                                                variant="inline"
                                                margin="normal"
                                                id="date-picker-end"
                                                label="End Date"
                                                value={inputs.endDate || null}
                                                onChange={date => handleFilterChange(date.toISOString(), 'endDate')}
                                                KeyboardButtonProps={{
                                                    'aria-label': 'change date',
                                                }}
                                                className={classes.formControl}
                                            />
                                        </MuiPickersUtilsProvider>
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <AutoCompleteInput
                                            options={allAffiliates}
                                            searchKey="id"
                                            keyVal="affiliateId"
                                            label="Affiliates"
                                            optionKeys={["name"]}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <AutoCompleteInput
                                            options={filterTrafficSources.length ? filterTrafficSources : allTrafficSources}
                                            searchKey="id"
                                            keyVal="trafficSourceId"
                                            label="Traffic Sources"
                                            optionKeys={['name', 'legacyPromoId']}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Container>
                        <Box paddingTop={3}>
                            <Grid container spacing={2} justify="center" alignItems="center">
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                    <Button type="submit" variant="contained" color="primary" fullWidth>
                                        Filter Results
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} lg={3}>
                                    <Button onClick={() => {
                                        clearAllInputs();
                                        setVariables(defaultQuery);
                                    }} variant="outlined" color="secondary" fullWidth>
                                        Clear Filters
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    </ExpansionPanelDetails>
                </ExpansionPanel>
            </form>
            <Grid container direction="row" justify="space-between" alignItems="center" style={{marginBottom: '15px'}}>
                <Grid item xs={4}>
                </Grid>
                <Grid item xs={4} style={{textAlign: 'right', margin: '10px 0'}}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate('/info/revenueitems/add')}
                        startIcon={<AddIcon/>}>
                        ADD REVENUE
                    </Button>
                </Grid>
            </Grid>

            <Paper className={classes.paper}>
                <Grid
                    container
                    spacing={1}
                    direction="row"
                    alignItems="center"
                    justify="flex-end"
                >
                    <Button onClick={() => {
                        setConfigColumns(activeColumns);
                        setOpen(true);
                    }} variant="contained" color="secondary">
                        Select Table Columns
                    </Button>
                    <IconButton aria-label="refetch results" onClick={() => refetch()}>
                        <RefreshIcon/>
                    </IconButton>
                    {csvData ? (

                        <CSVLink
                            data={csvData.data}
                            headers={csvData.headers}
                            style={{textDecoration: 'none'}}
                        >
                            <Button variant="contained" color="secondary">
                                Export Current Page
                            </Button>
                        </CSVLink>
                    ) : null}
                </Grid>
                <RevenueItemsTable/>
                <Grid
                    container
                    spacing={1}
                    direction="row"
                    alignItems="center"
                    justify="space-between"
                    className={classes.paginationContainer}
                >
                    <Grid item xs>
                        <Typography variant="body2">
                            Displaying {Math.min(paginationVal.totalCount, paginationVal.skipVal + 1)}-{Math.min(paginationVal.skipVal + paginationVal.limit, paginationVal.totalCount)} of {paginationVal.totalCount} Results
                        </Typography>
                    </Grid>
                    <Grid item xs>
                        {paginationVal.numberOfPages > 1 ? (
                            <Pagination
                                count={paginationVal.numberOfPages}
                                showFirstButton
                                showLastButton
                                color="primary"
                                onChange={(e, value) => {
                                    setPaginationVal({
                                        ...paginationVal,
                                        currentPage: value,
                                        skipVal: paginationVal.limit * (value - 1)
                                    });
                                }}
                                page={paginationVal.currentPage}
                            />
                        ) : null}
                    </Grid>
                </Grid>
                {open && (
                    <Modal
                        open={open}
                        onClose={handleClose}
                    >
                        <Container className={classes.modal} maxWidth={false}>
                            <Paper className={classes.paper} style={{marginBottom: 0}}>
                                <Typography variant={"h6"}>Select Table Columns</Typography>
                                <Grid
                                    container
                                    spacing={3}
                                    direction="row"
                                    alignItems="center"
                                >
                                    {activeColumns.map((column, idx) => (
                                        <Grid item xs={3} key={column.id}>
                                            <Typography
                                                variant={"body1"}><strong>{column.title}</strong></Typography>
                                            <Switch
                                                onChange={(e) => {
                                                    setConfigColumns(configColumns.map(item => (item.id === column.id ? Object.assign({}, item, {
                                                        ...item,
                                                        enabled: e.target.checked
                                                    }) : item)))
                                                }}
                                                checked={configColumns[idx].enabled}
                                                name={column.title}
                                                inputProps={{'aria-label': 'primary checkbox'}}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                                <Box
                                    display={"flex"}
                                    justifyContent={"center"}
                                    alignItems={"center"}
                                >
                                    <Button
                                        onClick={() => {
                                            setActiveColumns(configColumns);
                                            setOpen(false);
                                        }}
                                        variant="contained"
                                        color="primary"
                                    >
                                        Save
                                    </Button>
                                </Box>
                            </Paper>
                        </Container>
                    </Modal>
                )}
            </Paper>
        </>
    );
};

export default RevenueItems;
